import type { ReactNode } from "react";

import { HaNViewStoreContext, selectHoveredNodeId, selectSelection, useHaNView } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { cn } from "@utils";

import { HaNContext } from "../../../shell/HaNContext";

type HaNResultListItemProps = {
  nodeId: string;
  selectionId?: string;

  // Off when items share a node, like the people in one area, so hovering one item doesn't highlight the others
  highlightsOnMapHover?: boolean;

  onSelect: () => void;
  children: ReactNode;
};

const scrollIntoView = (item: HTMLLIElement | null) => {
  item?.scrollIntoView({ behavior: "smooth", block: "nearest" });
};

// Hovering spotlights the node on the map, and hovering it on the map highlights the item.
export const HaNResultListItem = ({
  nodeId,
  selectionId = nodeId,
  highlightsOnMapHover = true,
  onSelect,
  children,
}: HaNResultListItemProps) => {
  const { tab } = useRequiredContext(HaNContext);
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const isSelected = useHaNView(state => selectSelection(tab)(state)?.id === selectionId);
  const isHovered = useHaNView(state => highlightsOnMapHover && selectHoveredNodeId(state) === nodeId);

  return (
    <li ref={isSelected ? scrollIntoView : undefined}>
      <button
        type="button"
        aria-current={isSelected || undefined}
        className={cn(
          "flex w-full items-center rounded-lg border-2 border-transparent p-1.5 text-left focus-visible:border-mapiq-black-800* focus-visible:outline-none [@media(hover:hover)]:hover:bg-black/[0.03]",
          { "bg-black/[0.03]": isHovered || isSelected },
        )}
        onClick={onSelect}
        onMouseEnter={() => viewStore.hover(nodeId)}
        onMouseLeave={() => viewStore.hover(null)}>
        {children}
      </button>
    </li>
  );
};
